import React, { useState, useEffect } from 'react';
import { Container, Image, Dropdown } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from '../../../api/axios';
import CompanyDetailsModal from '../common/CompanyDetailsModal';
import ChangePasswordModal from '../common/ChangePasswordModal';
import './Header.css';
import { User } from '../../../types';
import { ApiError } from '../../../types/errors';

interface HeaderProps {
  user: User | null;
}

interface CompanyUpdateEvent extends Event {
  detail: {
    name: string;
    email: string;
    contact_person: string;
    phone: string;
    business_category1_id: number;
    business_category2_id: number;
    business_category3_id: number;
  };
}

declare global {
  interface WindowEventMap {
    companyUpdated: CompanyUpdateEvent;
  }
}

const Header: React.FC<HeaderProps> = ({ user }) => {
  const navigate = useNavigate();
  const [showCompanyDetailsModal, setShowCompanyDetailsModal] = useState<boolean>(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<User | null>(user);

  useEffect(() => {
    setCurrentUser(user);
  }, [user]);

  useEffect(() => {
    const handleCompanyUpdate = (event: CompanyUpdateEvent) => {
      setCurrentUser(prev => {
        if (!prev) return null;
        return {
          ...prev,
          name: event.detail.name,
          email: event.detail.email,
          contact_person: event.detail.contact_person,
          phone: event.detail.phone,
          business_category1_id: event.detail.business_category1_id,
          business_category2_id: event.detail.business_category2_id,
          business_category3_id: event.detail.business_category3_id
        };
      });
    };

    window.addEventListener('companyUpdated', handleCompanyUpdate);

    return () => {
      window.removeEventListener('companyUpdated', handleCompanyUpdate);
    };
  }, []);

  const handleLogout = async (): Promise<void> => {
    try {
      const token = localStorage.getItem('token');
      await axios.post('/logout', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error: unknown) {
      const axiosError = error as ApiError;
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/');
    }
  };

  return (
    <div className="header">
      <Container>
        <div className="logo-container">
          <Image src="/logo.svg" alt="ESF Logo" className={`logo-image ${currentUser && 'pointer'}`} onClick={() => { currentUser && navigate('/dashboard') }} />
          {currentUser && (
            <div className="user-dropdown">
              <Dropdown>
                <Dropdown.Toggle variant="link" id="dropdown-basic" className="company-name-dropdown">
                  {currentUser.name}
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => setShowCompanyDetailsModal(true)}>企業詳細</Dropdown.Item>
                  <Dropdown.Item onClick={() => setShowChangePasswordModal(true)}>パスワード変更</Dropdown.Item>
                  <Dropdown.Item onClick={() => navigate('/question-templates')}>質問テンプレート設定</Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={handleLogout}>ログアウト</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          )}
        </div>
      </Container>

      {/* Modals */}
      <CompanyDetailsModal 
        show={showCompanyDetailsModal} 
        onHide={() => setShowCompanyDetailsModal(false)} 
        user={currentUser} 
      />
      <ChangePasswordModal 
        show={showChangePasswordModal} 
        onHide={() => setShowChangePasswordModal(false)} 
      />
    </div>
  );
};

export default Header;
